import { useState } from 'react';
import AdminSidebar from '@/components/AdminSidebar';
import AdminProfileMenu from '@/components/AdminProfileMenu';
import AdminIntake from './AdminIntake';
import AdminPartners from './AdminPartners';
import { BRAND } from '@/brand';
import { Inbox, Handshake } from 'lucide-react';

/* Partners — one page, two tabs.
   ---------------------------------------------------------------------------
   These used to be two separate sidebar entries (/admin/intake and
   /admin/partners) that looked like duplicates but aren't:

     Applications — INBOUND. What people submitted through the public /partner
                    form. A queue you review.
     Roster       — OUTBOUND. The partner list you curate yourself (free zones,
                    law firms, banks) with commission rates and status.

   Both are still their own routes, so old links and bookmarks keep working. This
   page renders the shell and profile menu once and mounts each of them in
   `embedded` mode, which suppresses their own sidebar/topbar. Nothing about their
   data handling changed. */

const IS_CORP = BRAND === 'corporate';
type Tab = 'applications' | 'roster';

const TABS: { key: Tab; label: string; icon: typeof Inbox; hint: string }[] = [
  { key: 'applications', label: IS_CORP ? 'Applications' : 'Applications & intakes', icon: Inbox, hint: 'Submitted through the public form' },
  { key: 'roster', label: 'Partner roster', icon: Handshake, hint: 'The list you curate' },
];

export default function AdminPartnersHub() {
  const [tab, setTab] = useState<Tab>('applications');
  const accent = IS_CORP ? '#F08A24' : '#2563EB';

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex">
      <AdminSidebar />
      <div className="flex-1 xl:ml-64 overflow-x-hidden adminx">
        <style>{`
          .adminx { font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #0f172a; }
          .adminx * { box-sizing: border-box; }
          .pt-tabs { display: flex; gap: 8px; flex-wrap: wrap; }
          .pt-tab {
            display: flex; align-items: center; gap: 9px; padding: 10px 16px; border-radius: 12px;
            border: 1.5px solid #E6E9ED; background: #fff; cursor: pointer; font: inherit;
            font-size: 13.5px; font-weight: 700; color: #64748B; transition: .16s;
          }
          .pt-tab:hover { border-color: #CBD5E1; color: #0F172A; }
          .pt-tab.on { background: ${accent}; border-color: ${accent}; color: #fff; }
          .pt-tab small { display: block; font-weight: 500; font-size: 11px; opacity: .8; }
        `}</style>

        <header className="bg-white border-b border-[#E6E9ED] px-4 md:px-6 h-[58px] flex items-center justify-between sticky top-0 z-30">
          <span className="text-[13px] text-[#64748B] font-medium ml-10 xl:ml-0">
            Overview / <b className="text-[#0F172A] font-semibold">Partners</b>
          </span>
          <AdminProfileMenu />
        </header>

        <div className="page">
          <div style={{ marginBottom: 20 }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.02em' }}>Partners</h1>
            <p style={{ fontSize: 13.5, color: '#64748B', marginTop: 4 }}>
              Applications that came to you, and the roster you build yourself — in one place.
            </p>
          </div>

          <div className="pt-tabs" style={{ marginBottom: 22 }}>
            {TABS.map(({ key, label, icon: Icon, hint }) => (
              <button key={key} className={`pt-tab ${tab === key ? 'on' : ''}`} onClick={() => setTab(key)}>
                <Icon size={17} strokeWidth={2.1} />
                <span>{label}<small>{hint}</small></span>
              </button>
            ))}
          </div>

          {tab === 'applications' ? <AdminIntake embedded /> : <AdminPartners embedded />}
        </div>
      </div>
    </div>
  );
}
